// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";

import { ActionType, IAction, IActionSortTable, raiseAction } from "actions";
import { ITariffCircuit } from "shared/types/parameter";
import { isSet } from "shared/utils/utils";
import { makeSortHeader } from "guiutils";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";
import TariffCircuitListModel from "model/tariffcircuitlist";
import ChartBaseComponent, {
  IChartBaseDispatch,
  IChartBaseExtProps,
  IChartBaseProps,
  IChartBaseState,
  mapStateToProps as baseMapStateToProps,
  mapDispatchToProps as baseMapDispatchToProps,
  IChartTariffNode,
} from "./ChartBase";
import TariffCircuit from "view/comp/TariffCircuit";
import { Category } from "states/cachedstates";
import { HierarchySelectionState } from "./TreeViewDropDown";

// sort key of table
const TABLE_NAME = "t_tariffcircuits";

interface ITariffCircuitListExtProps extends IChartBaseExtProps {}

interface ITariffCircuitListProps extends IChartBaseProps {
  tableSort: ITableSort | undefined;
  showId: boolean;
}

interface ITariffCircuitListState extends IChartBaseState {}

interface ITariffCircuitListDispatch extends IChartBaseDispatch {
  onSort: (sort: IActionSortTable) => void;
  onCategoryChanged: (selection: Category | undefined) => void;
}

const mapStateToProps = (state: IState, ownProps: ITariffCircuitListExtProps) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: ITariffCircuitListProps = {
    ...baseMapStateToProps(state, ownProps),
    tableSort: state.gui.tableSort[TABLE_NAME],
    showId: Boolean(curUser?.accessRights.setVirtualParameters),
  };
  return props;
};

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITariffCircuitListDispatch = {
    ...baseMapDispatchToProps(dispatch),
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onCategoryChanged: (selection: Category | undefined) => {
      dispatch(raiseAction(ActionType.ACT_SELECT_CATEGORY, { selection }));
    },
  };
  return dispatchProps;
};

class TariffCircuitListComponent extends ChartBaseComponent<
  ITariffCircuitListProps & ITariffCircuitListExtProps & ITariffCircuitListDispatch,
  ITariffCircuitListState
> {
  // enable/disable gray-out of items based on hierarchy selection
  private readonly isHierarchyGrayOutEnabled = false;

  private handleTartiffClicked = (event: React.MouseEvent, clickedTariffIdIdx: number) => {
    if (this.props.isHierarchyViewMode === false && event.shiftKey === true) {
      return;
    }
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);

    const newSelectedTcIds = () => {
      if (this.props.isHierarchyViewMode === true) {
        return event?.metaKey === true || event?.ctrlKey === true ?
            //select parent
            tcModel.updateHierarchySelection(
              this.allTariffCircuits,
              [clickedTariffIdIdx],
              undefined,
            )
            // select this tariff as new root
          : this.prepareNextLevel(tcModel, clickedTariffIdIdx);
      }

      const allCurrentIds =
        isSet(this.props.selection?.tariffCircuitIds) ?
          this.props.selection?.tariffCircuitIds
        : this.props.tariffCircuits.map((item, idx) => item.id ?? idx);

      const childIds =
        isSet(allCurrentIds.find((tcId) => tcId === clickedTariffIdIdx)) ?
          (allCurrentIds.filter((tcId) => clickedTariffIdIdx !== tcId) as number[])
        : (allCurrentIds.concat([clickedTariffIdIdx]) as number[]);

      return childIds;
    };

    this.props.onHierarchyChanged(newSelectedTcIds(), this.props.isHierarchyViewMode ?? true);
  };

  private handleRootItemClicked = (event: React.MouseEvent, clickedTariffIdIdx: number) => {
    if (
      this.props.isHierarchyViewMode === false &&
      event.metaKey === false &&
      event.ctrlKey === false
    ) {
      return;
    }
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);

    const newSelectedTcIds = () => {
      if (this.props.isHierarchyViewMode === true) {
        return event?.metaKey === true || event?.ctrlKey === true ?
            //select parent
            tcModel.updateHierarchySelection(
              this.allTariffCircuits,
              [clickedTariffIdIdx],
              undefined,
            )
            // select this tariff as new root
          : this.prepareNextLevel(tcModel, clickedTariffIdIdx);
      }

      const allCurrentIds =
        isSet(this.props.selection?.tariffCircuitIds) ?
          this.props.selection?.tariffCircuitIds
        : this.props.tariffCircuits.map((item, idx) => item.id ?? idx);

      const childIds =
        isSet(allCurrentIds.find((tcId) => tcId === clickedTariffIdIdx)) ?
          (allCurrentIds.filter((tcId) => clickedTariffIdIdx !== tcId) as number[])
        : (allCurrentIds.concat([clickedTariffIdIdx]) as number[]);

      return childIds;
    };

    this.props.onHierarchyChanged(newSelectedTcIds(), this.props.isHierarchyViewMode ?? true);
  };

  private renderSortedListGroup(
    tlList: IChartTariffNode[],
    showHierarchy: boolean,
    showCategory: boolean,
    activeRootTarrifId?: number,
  ) {
    const tariffCircuits = this.props.tariffCircuits;
    const sortCol = (this.props.tableSort?.column ?? "name") as keyof ITariffCircuit | "value";
    const tcMap = new Map(tariffCircuits.map((tc, idx) => [tc.id ?? idx, tc]));
    const circuitItems = tlList
      .filter((item) => !this.isHierarchyGrayOutEnabled || item.id !== activeRootTarrifId)
      .sort((a, b) => {
        const tcA = tcMap.get(a.id);
        const tcB = tcMap.get(b.id);
        const keyA = sortCol === "value" ? a.value : tcA?.[sortCol!];
        const keyB = sortCol === "value" ? b.value : tcB?.[sortCol!];
        const sort =
          (isSet(keyA) && isSet(keyB) && keyA > keyB) || (isSet(keyA) && !isSet(keyB)) ? 1 : -1;
        return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
      })
      .map((aTL) => {
        // return a React-Component...
        return (
          <TariffCircuit
            tarifflineInfo={aTL}
            key={aTL.id}
            showHierarchy={showHierarchy}
            showCategory={showCategory}
            trendMember={this.props.trendMember}
            filterText={this.props.filterText}
            selectedCategory={this.props.selection?.category}
            activeRootId={activeRootTarrifId}
            dropDownHierarchyMode={this.props.isHierarchyViewMode}
            isDashboard={this.props.isDashboard}
            onTariffClicked={
              this.isHierarchyGrayOutEnabled ? this.handleTartiffClicked.bind(this) : undefined
            }
            onCategoryClicked={
              this.isHierarchyGrayOutEnabled ?
                (event, inCategory) => this.props.onCategoryChanged(inCategory)
              : undefined
            }
          />
        );
      });

    const rootItems = tlList
      .filter((item) => this.isHierarchyGrayOutEnabled && item.id === activeRootTarrifId)
      .map(
        (
          aTL, // return a React-Component...
        ) => (
          <TariffCircuit
            tarifflineInfo={aTL}
            key={aTL.id}
            showHierarchy={showHierarchy}
            showCategory={showCategory}
            trendMember={this.props.trendMember}
            filterText={this.props.filterText}
            selectedCategory={this.props.selection?.category}
            activeRootId={activeRootTarrifId}
            dropDownHierarchyMode={this.props.isHierarchyViewMode}
            isDashboard={this.props.isDashboard}
            onTariffClicked={
              this.isHierarchyGrayOutEnabled ? this.handleRootItemClicked.bind(this) : undefined
            }
            onCategoryClicked={
              this.isHierarchyGrayOutEnabled ?
                (event, inCategory) => this.props.onCategoryChanged(inCategory)
              : undefined
            }
          />
        ),
      );

    // render the selection-boxes and the whole table(s)
    return (
      <table className="datatable fullwidthwhite">
        {circuitItems.length > 0 && (
          <thead>
            <tr>
              {this.props.showId && <th className="optional-lg">Id</th>}
              {makeSortHeader(
                TABLE_NAME,
                "name",
                <FormattedMessage id="Name" />,
                this.props.tableSort ?? { column: "name", isSortAscending: true },
                this.props.onSort,
              )}
              {showHierarchy &&
                makeSortHeader(
                  TABLE_NAME,
                  "parent",
                  <FormattedMessage id="Parent" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["optional-lg"],
                )}
              {showCategory &&
                makeSortHeader(
                  TABLE_NAME,
                  "categories",
                  <FormattedMessage id="Category" />,
                  this.props.tableSort,
                  this.props.onSort,
                  ["optional-lg"],
                )}
              {makeSortHeader(
                TABLE_NAME,
                "value",
                <FormattedMessage id="Value" />,
                this.props.tableSort,
                this.props.onSort,
                ["value-col", "center"],
              )}
            </tr>
          </thead>
        )}
        <tbody>{[...rootItems, ...circuitItems]}</tbody>
      </table>
    );
  }

  public render() {
    // check if a hierarchy and/or a category is available
    const hasHierarchy = TariffCircuitListModel.hasHierarchy(this.props.tariffCircuits);
    const hasCategory = TariffCircuitListModel.hasCategories(this.props.tariffCircuits);

    // must be called in every derrived class from ChartBase!!!
    // the field allTariffCircuits is getting prepared within that function
    this.prepareHierarchyData();

    const { selectedTariffs, tlOptions } = this.getSelectedTariffCircuits(
      // show all tariffs when not in hierarchy mode?
      !this.isHierarchyGrayOutEnabled || this.props.isHierarchyViewMode !== true,
      false, // show only selected categories in color
      true, // show all entries in color
    );

    const activeRootTarrifId =
      this.props.isHierarchyViewMode === true ?
        tlOptions.find((tl) => tl.checked === HierarchySelectionState.SELECTED)
      : undefined;
    // render all tables for all energy-sources
    return (
      <div id="tariffcircuits">
        {selectedTariffs.length > 0 ?
          <React.Fragment key={this.props.selectedEnergySource}>
            {this.renderSortedListGroup(
              selectedTariffs,
              hasHierarchy,
              hasCategory,
              activeRootTarrifId?.referenceEntry?.id ?? activeRootTarrifId?.entryId?.at(-1),
            )}
          </React.Fragment>
        : <FormattedMessage id="No tariff circuits present." />}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TariffCircuitListComponent);
